import {jwtDecode} from 'jwt-decode';
import {exchangeEmailWithCode} from '../store/slices/exchange_token_slice';
import {redirect} from 'react-router-dom';
import {isFulfilled, isRejected} from '@reduxjs/toolkit';
import {store as AppStore} from '../store/store';
import {loginRedirectRoute} from '../support/login_redirect_route.ts';
import {showErrorToast} from '../store/toast_error_middleware.ts';
import {consumePendingRedirect, storeExpiredLinkContext} from '../support/expired_login_link.ts';

interface LoginCredentials {
    email: string;
    code: string;
}

function decodeLoginToken(token: string): LoginCredentials | null {
    try {
        const decodedToken = jwtDecode(token);
        if (
            'code' in decodedToken &&
            'email' in decodedToken &&
            typeof decodedToken.email === 'string' &&
            typeof decodedToken.code === 'string'
        ) {
            return {email: decodedToken.email, code: decodedToken.code};
        }
    } catch (error) {
        console.warn(error);
    }
    return null;
}

function redirectForInvalidLink(hasSession: boolean, redirectTo: string | null, email: string | null) {
    if (hasSession) {
        return redirect(loginRedirectRoute({}, redirectTo));
    }

    storeExpiredLinkContext(redirectTo, email);
    return redirect('/login/email?expired=1');
}

export async function tokenLoader(store: typeof AppStore, url: string) {
    const searchParams = new URL(url).searchParams;
    const token = searchParams.get('token');
    if (!token) {
        return null;
    }

    const redirectTo = searchParams.get('redirect');
    const hasSession = store.getState().exchangeToken.token !== null;
    const credentials = decodeLoginToken(token);

    if (credentials === null) {
        return redirectForInvalidLink(hasSession, redirectTo, null);
    }

    const result = await store.dispatch(exchangeEmailWithCode({...credentials, silent: true}));

    if (isFulfilled(result) && result.payload.login?.token != null) {
        localStorage.setItem('token', result.payload.login.token);
        const pendingRedirect = consumePendingRedirect();
        return redirect(loginRedirectRoute(result.payload.login, redirectTo || pendingRedirect));
    }

    if (isRejected(result) && result.error.message?.toLowerCase().includes('customer not found')) {
        return redirectForInvalidLink(hasSession, redirectTo, credentials.email);
    }

    if (hasSession) {
        return redirect(loginRedirectRoute({}, redirectTo));
    }

    showErrorToast();
    return redirect('/login');
}
